/**
 * Compresse et redimensionne une image pour réduire sa taille
 * @param file - Le fichier image à compresser
 * @param maxWidth - Largeur maximale (défaut: 1920)
 * @param maxHeight - Hauteur maximale (défaut: 1920)
 * @param quality - Qualité JPEG/WebP (0-1, défaut: 0.85)
 * @param maxSizeBytes - Taille maximale en bytes (défaut: 4MB pour laisser une marge)
 * @returns Promise<File> - Le fichier compressé
 */
export async function compressImage(
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.85,
  maxSizeBytes: number = 4 * 1024 * 1024, // 4MB par défaut
): Promise<File> {
  return new Promise((resolve, reject) => {
    // Si le fichier est déjà assez petit, le retourner tel quel
    if (file.size <= maxSizeBytes) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculer les nouvelles dimensions en conservant le ratio
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        // Créer un canvas pour redimensionner et compresser
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Impossible de créer le contexte canvas'));
          return;
        }

        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir en blob avec compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Impossible de compresser l\'image'));
              return;
            }

            // Si le blob est encore trop grand, réduire la qualité progressivement
            if (blob.size > maxSizeBytes && quality > 0.5) {
              // Réessayer avec une qualité réduite
              canvas.toBlob(
                (compressedBlob) => {
                  if (!compressedBlob) {
                    reject(new Error('Impossible de compresser l\'image'));
                    return;
                  }
                  const compressedFile = new File(
                    [compressedBlob],
                    file.name,
                    { type: file.type || 'image/jpeg' }
                  );
                  resolve(compressedFile);
                },
                file.type || 'image/jpeg',
                quality * 0.7 // Réduire encore la qualité
              );
            } else {
              const compressedFile = new File(
                [blob],
                file.name,
                { type: file.type || 'image/jpeg' }
              );
              resolve(compressedFile);
            }
          },
          file.type || 'image/jpeg',
          quality
        );
      };
      img.onerror = () => {
        reject(new Error('Impossible de charger l\'image'));
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error('Impossible de lire le fichier'));
    };
    reader.readAsDataURL(file);
  });
}

